/**
 * Single source of truth for “may the deck open for this member?”
 *
 * Approval is written to applications / review queue (and server profiles.review_status).
 * The matching session historically had no media/review field — so the gate must
 * read applications/review explicitly. Missing data is never treated as approved.
 */

import type { VerificationStatus } from "../domain/types";

const APPS_KEY = "un8.applications.v1";
const QUEUE_KEY = "un8.reviewQueue.v1";

type StoredApp = {
  id?: string;
  userId?: string;
  status?: string;
  verification?: { status?: string };
};

type QueueItem = {
  applicationId?: string;
  userId?: string;
  status?: string;
};

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/**
 * Local verification status for a user id.
 * Returns null when no application/queue row exists — caller must NOT treat null as approved.
 */
export function readLocalVerificationStatus(userId: string): VerificationStatus | null {
  if (!userId) return null;

  const apps = readJson<Record<string, StoredApp>>(APPS_KEY, {});
  const app =
    apps[userId] ||
    Object.values(apps).find((a) => a?.id === userId || a?.userId === userId);
  const fromApp = app?.verification?.status;
  if (
    fromApp === "approved" ||
    fromApp === "rejected" ||
    fromApp === "needs_resubmit" ||
    fromApp === "pending_review" ||
    fromApp === "draft"
  ) {
    return fromApp === "draft" ? "pending_review" : fromApp;
  }
  // Application row marked verified without nested status — treat as approved only then.
  if (app?.status === "verified") return "approved";
  if (app?.status === "rejected") return "rejected";
  if (app?.status === "pending_review") return "pending_review";

  const queue = readJson<QueueItem[]>(QUEUE_KEY, []);
  const item = queue.find(
    (q) => q.applicationId === userId || q.userId === userId
  );
  const fromQueue = item?.status;
  if (
    fromQueue === "approved" ||
    fromQueue === "rejected" ||
    fromQueue === "needs_resubmit" ||
    fromQueue === "pending_review"
  ) {
    return fromQueue;
  }

  return null;
}

/**
 * Merge server reviewStatus with local applications/review queue.
 * Explicit local approved opens the deck; missing local never invents approval.
 */
export function effectiveReviewStatus(
  selfId: string,
  serverReviewStatus: string | null | undefined
): string | null {
  const local = readLocalVerificationStatus(selfId);
  if (local === "approved") return "approved";
  if (local === "rejected" || local === "needs_resubmit") return local;
  if (local === "pending_review") {
    // Local pending wins over a stale empty server field, but if server already
    // approved, trust server (moderator path).
    if (serverReviewStatus === "approved") return "approved";
    return "pending_review";
  }
  // No local record — trust server only when it has an explicit status.
  if (serverReviewStatus) return serverReviewStatus;
  return null;
}

/** Demo / guest sessions — no ID case, never wait on moderation. */
export function isGuestUserId(userId: string | null | undefined): boolean {
  return Boolean(userId && userId.startsWith("user_guest_"));
}

/** Deck may open only on explicit approved — never on null/undefined. */
export function isReviewApprovedForDeck(
  selfId: string,
  serverReviewStatus: string | null | undefined
): boolean {
  if (isGuestUserId(selfId)) return true;
  return effectiveReviewStatus(selfId, serverReviewStatus) === "approved";
}

export function isModerationBlocking(
  selfId: string,
  serverReviewStatus: string | null | undefined,
  serverDeckReason: string | null | undefined
): boolean {
  // Guests have no review case — moderation hold can never clear. Never block.
  if (isGuestUserId(selfId)) return false;
  if (isReviewApprovedForDeck(selfId, serverReviewStatus)) return false;
  const effective = effectiveReviewStatus(selfId, serverReviewStatus);
  if (effective && effective !== "approved") return true;
  if (!serverDeckReason) return false;
  return /moderat|approval|pending|waiting for|complete and submit/i.test(
    serverDeckReason
  );
}
